import type { PropertiesHyphen } from 'csstype'
import React, { PropsWithChildren, ReactElement } from 'react'
import Sticky from 'react-sticky-el'
import styled, { useTheme } from 'styled-components'

import { useScreen } from '@hozana/screen/useScreen'
import { mediaQuery } from '@hozana/styling/responsive'
import { QA } from '@hozana/tests/constants'

import { Div, TDivProps } from 'elements/layout/Div'
import { Ellipsis } from 'elements/text/Ellipsis'
import { Link, TLinkProps } from 'elements/text/Link'
import { Tab } from 'elements/ui/Tab'
import { TTabListProps, TabList } from 'elements/ui/TabList'

import type { TUseTabs } from './useTabs'

const stickyClassName = 'on-sticky'

type TNavProps = { hideShadowOnDesktop?: boolean } & TDivProps

const Nav = styled(Div).attrs<TNavProps, TNavProps>({ as: 'nav' })`
  ${(props) => props.hideShadowOnDesktop && mediaQuery('xs', 'xs')} {
    box-shadow: 0 3px 3px rgba(0, 0, 0, 0.2);
    background: ${(props) => props.theme.colors.white};
  }
`

type TWrapperToFixMinHeightProps = { m?: PropertiesHyphen['margin'] }

const WrapperToFixMinHeight = styled.div<TWrapperToFixMinHeightProps>`
  & > div {
    min-height: 48px !important;
    margin: ${(props) => props.m};
  }
  & .${stickyClassName} ${Nav} {
    box-shadow: 0 3px 3px rgba(0, 0, 0, 0.2);
    background: ${(props) => props.theme.colors.white};
  }
  & .${stickyClassName} ${Tab} {
    border: none;
  }
  ${mediaQuery('xs', 'xs')} {
    ${Tab} {
      border: none;
    }
  }
`

export type TTabsBarProps<D extends Record<string, string> = Record<string, string>> = {
  dataQaIds?: Partial<Record<keyof D, string>>
} & Pick<TTabListProps<D>, 'data' | 'maxW'> &
  Pick<TLinkProps, 'asButton'> &
  TUseTabs<D> &
  Pick<TNavProps, 'hideShadowOnDesktop' | 'p'> &
  TWrapperToFixMinHeightProps

/**
 * ### TabsBar ###
 */
export const TabsBar = <
  D extends {
    [i: string]: string
  },
>({
  data,
  m,
  p,
  maxW,
  currentTab,
  changeTab,
  getTabUrl,
  asButton,
  hideShadowOnDesktop = true,
  dataQaIds,
}: PropsWithChildren<TTabsBarProps<D>>): ReactElement<any, any> => {
  const numTabs = Object.keys(data).length
  const theme = useTheme()
  const screen = useScreen()
  return (
    <WrapperToFixMinHeight m={m}>
      <Sticky
        stickyStyle={{
          zIndex: theme.zIndex.stickyTabs,
          position: 'relative',
        }}
        stickyClassName={stickyClassName}
        disabled={screen.lg || screen.md}
        positionRecheckInterval={50}
      >
        <Nav w="100%" p={p} hideShadowOnDesktop={hideShadowOnDesktop}>
          <TabList maxW={maxW} m="auto" data={data} currentTab={currentTab}>
            {Object.keys(data).map((tabId: keyof D) => (
              <Tab maxW={1 / numTabs} selected={tabId === currentTab} w="100%" key={tabId.toString()}>
                <Link
                  asButton={asButton}
                  displayBlock
                  shallow
                  data-testid={dataQaIds?.[tabId] || QA.COMMON.TAB_LINK(tabId as string)}
                  to={getTabUrl(tabId)}
                  onClick={(e) => changeTab(tabId, e)}
                  w="100%"
                  h="50px" // to prevent error
                >
                  <Ellipsis lineHeight="2em" fontSize="1em" bold={tabId === currentTab}>
                    {data[tabId]}
                  </Ellipsis>
                </Link>
              </Tab>
            ))}
          </TabList>
        </Nav>
      </Sticky>
    </WrapperToFixMinHeight>
  )
}
